import React, { useEffect, useState } from "react";
import axios from "../axiosConfig";
import Modal from "../components/Modal";
import { typeIcons } from "../components/Utils";
import { useTaskRefresh } from "../context/TaskRefreshContext.jsx";
import "./Import.css";
import PageTitle from "../components/PageTitle";

export default function CreateHandouts() {
  const refreshTasks = useTaskRefresh();

  const [modalOpen, setModalOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const resetUploadState = () => {
    setFile(null);
    setDragActive(false);
    setUploading(false);
    setError(null);
    setSuccessMessage(null);
  };

  const handleOpenModal = () => {
    resetUploadState();
    setModalOpen(true);
  };

  // Open the upload modal automatically as soon as the page loads.
  useEffect(() => {
    handleOpenModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCloseModal = () => {
    setModalOpen(false);
    resetUploadState();
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const uploadFile = async (fileToUpload) => {
    if (!fileToUpload || uploading) return;

    setUploading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const formData = new FormData();
      formData.append("file", fileToUpload);

      await axios.post("/jobs/generate-handout/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });

      refreshTasks();
      setSuccessMessage(
        "Handout generation started — check Tasks for progress.",
      );
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Error uploading PPTX",
      );
    } finally {
      setUploading(false);
    }
  };

  const validateAndSetFile = (candidate) => {
    setError(null);
    setSuccessMessage(null);

    if (!candidate) return;

    const fileExt = candidate.name.toLowerCase().split(".").pop();
    if (fileExt !== "pptx") {
      setError("Please select a PPTX file");
      return;
    }

    setFile(candidate);
    uploadFile(candidate);
  };

  const handleFileSelect = (event) => {
    validateAndSetFile(event.target.files[0]);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  return (
    <div className="page-layout">
      <PageTitle>
        {typeIcons["createhandouts"]} Create Handouts
      </PageTitle>

      <div className="page-content">
        <p>
          Upload a PowerPoint deck to generate a matching Word handout — one
          page per slide.
        </p>

        <button
          className="select-file-btn"
          type="button"
          onClick={handleOpenModal}
          style={{ alignSelf: "flex-start" }}
        >
          Upload PowerPoint File
        </button>

        <Modal
          isOpen={modalOpen}
          onClose={handleCloseModal}
          title="Upload powerpoint file"
        >
          <div className="form-section">
            <p>Only accepts .pptx files.</p>

            <div
              className={`file-upload-area ${dragActive ? "drag-active" : ""}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                autoComplete="off"
                id="handout-file-input"
                type="file"
                accept=".pptx"
                onChange={handleFileSelect}
                style={{ display: "none" }}
              />

              {!file ? (
                <div className="upload-prompt">
                  <i className="fas fa-cloud-upload-alt upload-icon"></i>
                  <p>Drag and drop your .pptx file here, or</p>
                  <button
                    className="select-file-btn"
                    onClick={() =>
                      document.getElementById("handout-file-input").click()
                    }
                    type="button"
                  >
                    Select File
                  </button>
                  <small>.pptx files are accepted</small>
                </div>
              ) : (
                <div className="file-selected">
                  <i className="fas fa-file-powerpoint file-icon"></i>
                  <div className="file-info">
                    <p className="file-name">{file.name}</p>
                    <p className="file-size">{formatFileSize(file.size)}</p>
                  </div>
                  <button
                    className="clear-file-btn"
                    onClick={resetUploadState}
                    type="button"
                    title="Clear"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              )}
            </div>

            {uploading && (
              <div className="processing-results" style={{ marginTop: 12 }}>
                <p>Uploading...</p>
              </div>
            )}

            {error && (
              <div className="error-message" style={{ marginTop: 12 }}>
                <i className="fas fa-exclamation-triangle"></i>
                {error}
              </div>
            )}

            {successMessage && (
              <div className="processing-results" style={{ marginTop: 12 }}>
                <div className="success-message">
                  <i className="fas fa-info-circle"></i>
                  {successMessage}
                </div>
              </div>
            )}
          </div>
        </Modal>
      </div>
    </div>
  );
}
